import React, { useState } from "react";
import { useChannelsPost } from "../../useChannelsPost";
import {
  Form,
  Upload,
  Button,
  Tooltip,
  message,
  Typography,
  Space,
  Card,
  Row,
  Col,
  // Modal,
} from "antd";
import {
  UploadOutlined,
  CloseCircleFilled,
  EyeOutlined,
  DeleteOutlined,
  FileImageOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import CustomModal from "Components/CommonComponents/CustomModal";
import PDFPageviewer from "Pages/PlannerView/PDFPageviewer";
import "../../index.css";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import {
  isImageFile,
  isPdfFile,
  resolveAssetUrl,
} from "utilFunctions/shared/fileUtils";
import { cdnUrl } from "utilFunctions/Constants/urlConstants";

const Attachments = ({
  form,
  selectedPost,
  onDeleteAttachment,
  isEditDisable,
  hasFullAccess,
}: any) => {
  const { screen } = useWindowSize();
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState("");
  const [selectedPdfName, setSelectedPdfName] = useState("");
  const [selectedAssetType, setSelectedAssetType] = useState<
    "pdf" | "image" | null
  >(null);
  const { handleCancelUpload } = useChannelsPost();

  const handleViewPdf = (url: string, name: string) => {
    setSelectedPdfUrl(url);
    setSelectedPdfName(name);
    setSelectedAssetType("pdf");
    setPdfModalVisible(true);
  };

  const handleViewImage = (url: string, name: string) => {
    setSelectedPdfUrl(url);
    setSelectedPdfName(name);
    setSelectedAssetType("image");
    setPdfModalVisible(true);
  };

  const handleDeleteExistingAttachment = (attachmentId: string) => {
    if (onDeleteAttachment) {
      onDeleteAttachment(attachmentId);
    }
  };

  const renderExistingAttachments = () => {
    if (!selectedPost?.content || selectedPost.content.length === 0) {
      return null;
    }

    return (
      <div style={{ marginBottom: "16px" }}>
        <Typography.Text strong>Saved Attachments</Typography.Text>
        <Row gutter={[16, 16]} style={{ marginTop: "8px" }}>
          {selectedPost.content.map((attachment: any) => (
            <Col key={attachment.id} xs={12} sm={12} md={8} lg={6}>
              <Card
                size="small"
                actions={[
                  <Tooltip title="View" key={attachment.id + "view"}>
                    <Button
                      type="text"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        if (attachment.assetType === "Ebook") {
                          handleViewPdf(
                            resolveAssetUrl(attachment.url, cdnUrl),
                            attachment.url.split("/").pop() || ".pdf"
                          );
                        } else {
                          handleViewImage(
                            resolveAssetUrl(attachment.url, cdnUrl),
                            attachment.url.split("/").pop() || ".image"
                          );
                        }
                      }}
                    />
                  </Tooltip>,
                  hasFullAccess && (
                    <Tooltip title="Delete" key={attachment.id + "Delete"}>
                      <Button
                        type="text"
                        danger
                        disabled={isEditDisable}
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          handleDeleteExistingAttachment(attachment.id)
                        }
                      />
                    </Tooltip>
                  ),
                ].filter(Boolean)}
              >
                <div style={{ textAlign: "center" }}>
                  {attachment.assetType === "Ebook" ? (
                    <FilePdfOutlined
                      style={{ fontSize: "24px", color: "#ff4d4f" }}
                    />
                  ) : (
                    <FileImageOutlined
                      style={{ fontSize: "24px", color: "#1890ff" }}
                    />
                  )}
                  <div
                    style={{
                      marginTop: "8px",
                      fontSize: "12px",
                      wordBreak: "break-word",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      height: "36px",
                    }}
                  >
                    {attachment.name
                      ? attachment.name || `${attachment.assetType} File`
                      : ""}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };
  const itemRender = (originNode: any, file: any) => {
    const isUploading = file.status === "uploading";
    return (
      <div className="ant-upload-list-item-container">
        {isUploading && handleCancelUpload && (
          <Tooltip title="Cancel Upload">
            <Button
              icon={<CloseCircleFilled />}
              onClick={() => handleCancelUpload(file)}
              style={{
                position: "absolute",
                top: "-5px",
                right: "-5px",
                zIndex: 1,
                background: "white",
                borderRadius: "50%",
                padding: 0,
                height: "20px",
                width: "20px",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              size="small"
              type="text"
              danger
            />
          </Tooltip>
        )}
        {originNode}
      </div>
    );
  };

  const normFile = (e: any) => {
    console.log("Upload event:", e);
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };
  const ebookLength = selectedPost?.content
    ? selectedPost.content.filter((att: any) => att.assetType === "Ebook")
        .length
    : 0;
  const imageLength = selectedPost?.content
    ? selectedPost.content.filter((att: any) => att.assetType === "Image")
        .length
    : 0;
  console.log("selectedPost in Attachments:", selectedPost, selectedPdfName);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {renderExistingAttachments()}
      {hasFullAccess && (
        <>
          <Typography.Text>Attachments (Optional)</Typography.Text>
          <Space className="d-flex flex-row align-items-start">
            <Form.Item
              getValueFromEvent={normFile}
              valuePropName="fileList"
              name="Image"
            >
              <Upload
                accept="image/*"
                multiple
                maxCount={5}
                beforeUpload={(file, fileList) => {
                  if (fileList.length + imageLength > 5) {
                    message.error("You can only upload up to 5 Image files!");
                    return Upload.LIST_IGNORE;
                  }
                  const isLt5MB = file.size / 1024 / 1024 < 5;
                  if (!isImageFile(file)) {
                    message.error("You can only upload image files!");
                    return Upload.LIST_IGNORE;
                  }
                  if (!isLt5MB) {
                    message.error("Image must be smaller than 5MB!");
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                itemRender={itemRender}
                onRemove={(file) => {
                  const newImageList = form
                    .getFieldValue("Image")
                    .filter((item: any) => item.uid !== file.uid);
                  form.setFieldsValue({ Image: newImageList });
                }}
              >
                <Button icon={<UploadOutlined />}>Add Images</Button>
              </Upload>
            </Form.Item>

            {/* <Form.Item
        getValueFromEvent={normFile}
        valuePropName="fileList"
        label="Videos (Optional)"
        name="videos"
      >
        <Upload
          accept=".mp4,.mov,.avi"
          multiple
          beforeUpload={(file) => {
            const isLt50MB = file.size / 1024 / 1024 < 50;
            const validTypes = [
              "video/mp4",
              "video/quicktime",
              "video/x-msvideo",
            ];
            if (!validTypes.includes(file.type)) {
              message.error(
                "You can only upload MP4, MOV, or AVI video files!"
              );
              return Upload.LIST_IGNORE;
            }
            if (!isLt50MB) {
              message.error("Video must be smaller than 50MB!");
              return Upload.LIST_IGNORE;
            }
            return false;
          }}
          itemRender={itemRender}
        >
          <Button icon={<UploadOutlined />}>Add Videos</Button>
        </Upload>
      </Form.Item> */}

            <Form.Item
              getValueFromEvent={normFile}
              valuePropName="fileList"
              name="Ebook"
            >
              <Upload
                accept=".pdf,application/pdf"
                multiple
                maxCount={5}
                beforeUpload={(file, fileList) => {
                  if (fileList.length + ebookLength > 5) {
                    message.error("You can only upload up to 5 PDF files!");
                    return Upload.LIST_IGNORE;
                  }
                  const isLt5MB = file.size / 1024 / 1024 < 5;
                  if (!isPdfFile(file)) {
                    message.error("You can only upload PDF files!");
                    return Upload.LIST_IGNORE;
                  }
                  if (!isLt5MB) {
                    message.error("PDF must be smaller than 5MB!");
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                itemRender={itemRender}
                onRemove={(file) => {
                  const newEbookList = form
                    .getFieldValue("Ebook")
                    .filter((item: any) => item.uid !== file.uid);
                  form.setFieldsValue({ Ebook: newEbookList });
                }}
              >
                <Button icon={<UploadOutlined />}>Add PDFs</Button>
              </Upload>
            </Form.Item>
          </Space>
          <Typography.Text
            style={{
              fontSize: "12px",
              color: "#888",
              marginTop: "-20px",
              marginBottom: "24px",
            }}
          >
            Supported formats: Images (JPG, PNG, GIF), PDFs. Max size: 5MB each.
          </Typography.Text>
        </>
      )}
      {/* PDF Viewer Modal */}
      <CustomModal
        modal={pdfModalVisible}
        centered={true}
        backdrop={true}
        size={"xl"}
        fullscreen={
          selectedAssetType === "pdf" ||
          ["xl", "lg", "md", "xxl"].includes(screen)
        }
        className={
          selectedAssetType === "pdf"
            ? "document-viewer-fullscreen-modal"
            : undefined
        }
        toggle={() => {
          setPdfModalVisible(false);
          setSelectedPdfName("");
          setSelectedPdfUrl("");
          setSelectedAssetType(null);
        }}
        title={selectedPdfName}
      >
        {selectedPdfUrl && selectedPdfUrl != "" && (
          <div
            className="modern-asset-viewer-container w-100 d-flex align-items-center justify-content-center"
            style={{
              height: "100vh",
              maxHeight: "100vh",
              overflow: "hidden",
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              padding: "0",
            }}
          >
            {selectedAssetType === "pdf" ? (
              <PDFPageviewer
                pdfUrl={selectedPdfUrl}
                allPageInOne={true}
                isLibraryView={true}
                parentComponent="ChannelAttachments"
              />
            ) : (
              <img
                src={selectedPdfUrl}
                alt={selectedPdfName || "Attachment preview"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          </div>
        )}
      </CustomModal>
    </div>
  );
};

export default Attachments;
